import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames/bind';
import { Panel } from 'components/Panel';
import { i18nKeys, useTranslation } from 'locales';
import { RECOVERY_PLANS_QUERY_KEY, useLoadRecoveryPlans } from 'modules/Settings/Workflows/api';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import MoveRecoveryPlanInvoices from 'shared/action-component/MoveRecoveryPlanInvoices';
import ChooseRecoveryPlanModal from 'shared/components/ChooseRecoveryPlanModal';
import MoveRecoveryPlanInvoiceModal from 'shared/components/MoveRecoveryPlanInvoiceModal';
import { Button, ButtonColor } from 'shared/io';
import { treatRecoveryPlan } from 'shared/serializer';
import { settingsActions } from 'store/settings/settings.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type { RecoveryPlan } from 'types/recovery-plan';
import type { StoreState } from 'types/storeTypes';
import InvoiceDetailContext from '../InvoiceDetail.context';
import styleIdentifiers from '../ToHandleDetail.module.scss';

const styles = classNames.bind(styleIdentifiers);

export default function InvoiceRecoveryPlan() {
  const { t } = useTranslation();
  const constants = useSelector((state: StoreState) => state.app.constants);

  const { data, reloadInfo } = useContext(InvoiceDetailContext);
  const { recoveryPlans = [] } = useLoadRecoveryPlans();
  const queryClient = useQueryClient();

  const setRecoveryPlan = async () => {
    const planId: string | null = await new Promise(resolve => {
      showDialog({
        id: DialogShowId.CUSTOM,
        size: DialogShowSize.MEDIUM,
        title: t(i18nKeys.PLAN.SELECT_PLAN),
        children: (
          <ChooseRecoveryPlanModal
            recoverPlans={recoveryPlans}
            onClick={selectedPlan => resolve(selectedPlan ?? null)}
          />
        ),
      });
    });

    if (!planId) {
      return;
    }

    const response: any = await new Promise(resolve => {
      settingsActions.getRecoveryPlan({
        id: Number(planId),
        callback: resolve,
      });
    });

    const { newPlan, out_of_plan, ...res } = await new Promise<any>(resolve => {
      settingsActions.getPlansInvoices({
        data: {
          invoice_ids: [data.id],
          scope: 'invoice',
        },
        callback: (props: any) =>
          resolve({
            ...props,
            newPlan: treatRecoveryPlan(response.data),
          }),
      });
    });

    const plans = res.data.map((plan: any) => treatRecoveryPlan(plan));
    const oldPlan: RecoveryPlan | null = plans.length > 0 ? plans[0] : null;

    const stepIndex = await new Promise<number | null>(resolve => {
      if (out_of_plan) {
        showDialog({
          id: DialogShowId.CUSTOM,
          size: DialogShowSize.LARGE,
          title: t(i18nKeys.PLAN.MOVE_INVOICES),
          children: <MoveRecoveryPlanInvoiceModal onSubmit={resolve} plan={newPlan} />,
        });
      } else {
        resolve(null);
      }
    });

    const stepsSwitch = await new Promise<any>(resolve => {
      if (data.recovery_step_id && oldPlan) {
        MoveRecoveryPlanInvoices({
          title: t(i18nKeys.PLAN.INVOICES),
          newPlan,
          oldPlan,
          currentStep: data.recovery_information.step_order,
          onSubmit: resolve,
        });
      } else {
        resolve([]);
      }
    });

    const plansSwitch: Array<any> = [];

    // Add step switch for existing plan if applicable
    if (data.recovery_step_id && oldPlan && stepsSwitch) {
      plansSwitch.push({
        from: data.recovery_plan_id,
        to: planId,
        steps_switch: stepsSwitch,
      });
    }

    // Add step switch for out-of-plan invoices if applicable
    if (stepIndex !== null) {
      plansSwitch.push({
        from: null,
        to: planId,
        steps_switch: [{ from: null, to: newPlan.recovery_steps[stepIndex].id }],
      });
    }

    settingsActions.switchPlansInvoices({
      id: Number(planId),
      data: {
        scope: 'invoice',
        plans_switch: plansSwitch,
        invoice_ids: [data.id],
      },
      callback: () => {
        (reloadInfo as any)();
        queryClient.invalidateQueries({ queryKey: RECOVERY_PLANS_QUERY_KEY });
        dialogHide(DialogShowId.CUSTOM);
      },
    });
  };

  // Sentry 1A0
  const sendingOptionAttributes = data.sending_options_attributes?.sending_media ?? [];

  const sendingMedia =
    sendingOptionAttributes?.length > 0
      ? data.sending_options_attributes.sending_media
      : (data.recovery_information?.sending_media ?? []);

  return (
    <Panel
      withBorder
      title={t(i18nKeys.RECOVERY_PLAN)}
      topRight={
        data.actions.indexOf('switch_plan') !== -1 && (
          <Button
            noMargin
            color={ButtonColor.GREY}
            label={t(i18nKeys.EDIT)}
            onClick={setRecoveryPlan}
          />
        )
      }
      className={styles('box', 'recovery-plan')}
    >
      {data.recovery_step_id ? (
        <>
          <div className={styles('item', 'block')}>
            <span className={styles('subtitle')}>{t(i18nKeys.NAME)}</span>
            <div className={styles('value', 'medium')}>
              {data.recovery_information.recovery_plan_name}
            </div>
          </div>
          <div className={styles('item', 'block')}>
            <span className={styles('subtitle')}> {t(i18nKeys.NEXT_STEP)}</span>
            <div className={styles('value', 'medium')}>
              <div>
                {t(i18nKeys.ONBOARDING.STEP)} {data.recovery_information.step_order + 1}
              </div>
            </div>
          </div>
          <div className={styles('item', 'block', 'subtitle-item')}>
            <span className={styles('subtitle')}>
              {data.recovery_information.task_title
                ? t(i18nKeys.COMMON.TASK)
                : t(i18nKeys.COMMON.TEMPLATE)}
            </span>
            <div className={styles('value', 'medium')}>
              <div>
                {data.recovery_information.template_name || data.recovery_information.task_title}
              </div>
            </div>
          </div>
          {!data.recovery_information.task_title && (
            <div className={styles('item', 'block', 'subtitle-item')}>
              <span className={styles('subtitle')}>{t(i18nKeys.DEBTOR.SENDING_MEDIUMS)}</span>
              <div className={styles('value', 'medium')}>
                <div>
                  {sendingMedia.map(
                    (sendingMedium, index) =>
                      `${index !== 0 ? ' - ' : ''}${
                        constants.sending_medium.find(item => sendingMedium.name === item.value)!
                          .description
                      }`,
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={styles('item', 'block')}>
          <span className={styles('subtitle')}>{t(i18nKeys.PLAN.EXECUTED)}</span>
          <div className={styles('value', 'medium')}>
            {data.recovery_information.recovery_plan_name}
          </div>
        </div>
      )}
    </Panel>
  );
}
